/**
 * RoutingDashboardPage - Dashboard de KPIs de Roteirizacao
 *
 * Exibe metricas e indicadores de performance:
 * - Entregas (total, taxa de conclusao, pontualidade)
 * - Eficiencia (distancia, tempo, otimizacao)
 * - Custos (total, por km, por entrega)
 * - Frota (utilizacao, top veiculos)
 * - Motoristas (eficiencia, ranking)
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Paper,
  LinearProgress,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  LocalShipping as DeliveryIcon,
  Speed as EfficiencyIcon,
  AttachMoney as CostIcon,
  DirectionsCar as FleetIcon,
  Person as DriverIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CompletedIcon,
  Cancel as FailedIcon,
  Schedule as PendingIcon,
  Route as RouteIcon,
  Refresh as RefreshIcon,
  DateRange as DateRangeIcon,
  EmojiEvents as TrophyIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { KpiCard } from './components';
import {
  generateMockKPIs,
  KPI_PERIODS,
  formatCurrency,
} from './services';
import { fetchKPIs } from './services/kpiApiService';

// =======================================
// Componentes Auxiliares
// =======================================

/**
 * Grafico de barras horizontal simples
 */
const HorizontalBarChart = ({ data = [], maxValue = null, tokens }) => {
  const max = maxValue || Math.max(...data.map((d) => d.value));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {data.map((item, index) => (
        <Box key={index}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px' }}>
              {item.name}
            </Typography>
            <Typography sx={{ color: tokens?.text?.primary, fontSize: '13px', fontWeight: 600 }}>
              {item.formattedValue || item.value}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(item.value / max) * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: tokens?.surface?.elevated,
              '& .MuiLinearProgress-bar': {
                backgroundColor: item.color || tokens?.accent?.primary,
                borderRadius: 4,
              },
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

/**
 * Grafico de pizza simples usando SVG
 */
const PieChart = ({ data = [], size = 120, tokens }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return null;

  let currentAngle = -90; // Comecar do topo

  const slices = data.map((item, index) => {
    const percentage = (item.value / total) * 100;
    const angle = (percentage / 100) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    // Calcular pontos do arco
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const radius = size / 2 - 5;
    const centerX = size / 2;
    const centerY = size / 2;

    const x1 = centerX + radius * Math.cos(startRad);
    const y1 = centerY + radius * Math.sin(startRad);
    const x2 = centerX + radius * Math.cos(endRad);
    const y2 = centerY + radius * Math.sin(endRad);

    const largeArc = angle > 180 ? 1 : 0;

    const pathD = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

    return (
      <path
        key={index}
        d={pathD}
        fill={item.color}
        stroke={tokens?.surface?.base}
        strokeWidth="2"
      />
    );
  });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <svg width={size} height={size}>
        {slices}
        {/* Centro branco para donut */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={size / 4}
          fill={tokens?.surface?.raised}
        />
      </svg>
      {/* Legenda */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {data.map((item, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: item.color,
              }}
            />
            <Typography sx={{ color: tokens?.text?.secondary, fontSize: '11px' }}>
              {item.name}
            </Typography>
            <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '11px' }}>
              ({Math.round((item.value / total) * 100)}%)
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/**
 * Lista de ranking
 */
const RankingList = ({ items = [], valueLabel = 'valor', tokens }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    {items.map((item, index) => (
      <Box
        key={item.id || index}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          backgroundColor: index === 0 ? `${tokens?.semantic?.warning}15` : tokens?.surface?.elevated,
          borderRadius: tokens?.radius?.sm,
          border: index === 0 ? `1px solid ${tokens?.semantic?.warning}40` : 'none',
        }}
      >
        {/* Posicao */}
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: index === 0 ? tokens?.semantic?.warning : tokens?.surface?.raised,
            color: index === 0 ? '#fff' : tokens?.text?.secondary,
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          {index + 1}
        </Box>

        {/* Nome */}
        <Typography
          sx={{
            flex: 1,
            color: tokens?.text?.primary,
            fontSize: '13px',
            fontWeight: index === 0 ? 600 : 400,
          }}
        >
          {item.name}
        </Typography>

        {/* Valor */}
        <Typography
          sx={{
            color: index === 0 ? tokens?.semantic?.warning : tokens?.text?.secondary,
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          {item.formattedValue || item.value} {valueLabel}
        </Typography>
      </Box>
    ))}
  </Box>
);

// =======================================
// Componente Principal
// =======================================

const RoutingDashboardPage = () => {
  const navigate = useNavigate();
  const tokens = usePremiumTokens();

  // Estado
  const [selectedPeriod, setSelectedPeriod] = useState(KPI_PERIODS.WEEK);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState(generateMockKPIs());

  // Carregar KPIs da API
  const loadKPIs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchKPIs(selectedPeriod);
      setKpis(data);
    } catch (err) {
      console.error('Failed to load KPIs:', err);
      setError(err.message);
      // Usar dados mock em caso de erro
      setKpis(generateMockKPIs());
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod]);

  // Carregar dados iniciais e quando periodo muda
  useEffect(() => {
    loadKPIs();
  }, [loadKPIs]);

  // Periodo labels
  const periodLabels = {
    [KPI_PERIODS.TODAY]: 'Hoje',
    [KPI_PERIODS.WEEK]: 'Esta Semana',
    [KPI_PERIODS.MONTH]: 'Este Mes',
    [KPI_PERIODS.QUARTER]: 'Este Trimestre',
    [KPI_PERIODS.YEAR]: 'Este Ano',
  };

  // Handler de refresh
  const handleRefresh = () => {
    loadKPIs();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: tokens?.surface?.base,
        pb: 4,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${tokens?.border?.subtle}`,
          backgroundColor: tokens?.surface?.raised,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              size="small"
              onClick={() => navigate(-1)}
              sx={{ color: tokens?.text?.secondary }}
            >
              <BackIcon />
            </IconButton>
            <Box>
              <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, fontSize: '18px' }}>
                Dashboard de Roteirizacao
              </Typography>
              <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
                Indicadores de Performance (KPIs)
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Seletor de periodo */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                startAdornment={<DateRangeIcon sx={{ mr: 1, color: tokens?.text?.tertiary }} />}
                sx={{
                  backgroundColor: tokens?.surface?.base,
                  '& .MuiSelect-select': {
                    py: 1,
                  },
                }}
              >
                {Object.entries(periodLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Refresh */}
            <Tooltip title="Atualizar dados">
              <IconButton
                onClick={handleRefresh}
                disabled={isLoading}
                sx={{ color: tokens?.text?.secondary }}
              >
                {isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2, maxWidth: 1600, mx: 'auto' }}>
        {/* Demo Mode Alert */}
        {kpis?.meta?.isDemo && (
          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{ mb: 3, borderRadius: tokens?.radius?.md }}
          >
            Exibindo dados de demonstracao. Crie rotas para visualizar seus dados reais.
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert
            severity="warning"
            sx={{ mb: 3, borderRadius: tokens?.radius?.md }}
            onClose={() => setError(null)}
          >
            Erro ao carregar dados: {error}. Exibindo dados de demonstracao.
          </Alert>
        )}

        {/* Loading Overlay */}
        {isLoading && (
          <LinearProgress
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 9999,
            }}
          />
        )}

        {/* KPIs Principais */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<DeliveryIcon />}
              label="Total de Entregas"
              value={kpis.deliveries.total.toLocaleString()}
              description={`${kpis.deliveries.completed} concluidas`}
              trend={{ changePercent: 12.5, direction: 'up' }}
              isGoodWhenUp={true}
              chartData={kpis.trends.deliveries}
              chartColor={tokens?.semantic?.success}
              variant="highlight"
              size="large"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<CompletedIcon />}
              label="Taxa de Conclusao"
              value={kpis.deliveries.completionRate}
              suffix="%"
              description="Meta: 95%"
              trend={{ changePercent: 2.3, direction: 'up' }}
              isGoodWhenUp={true}
              variant="default"
              size="large"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<CostIcon />}
              label="Custo Total"
              value={formatCurrency(kpis.costs.totalCost)}
              description={`${formatCurrency(kpis.costs.costPerDelivery)}/entrega`}
              trend={{ changePercent: -5.2, direction: 'down' }}
              isGoodWhenUp={false}
              chartData={kpis.trends.costs}
              chartColor={tokens?.semantic?.warning}
              variant="default"
              size="large"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<EfficiencyIcon />}
              label="Eficiencia de Rota"
              value={kpis.efficiency.routeUtilization}
              suffix="%"
              description={`${kpis.efficiency.optimizationSavingsPercent}% economia`}
              trend={{ changePercent: 1.8, direction: 'up' }}
              isGoodWhenUp={true}
              variant="default"
              size="large"
            />
          </Grid>
        </Grid>

        {/* Tabs de Categorias */}
        <Box sx={{ borderBottom: `1px solid ${tokens?.border?.subtle}`, mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                minHeight: 48,
                fontWeight: 500,
              },
              '& .Mui-selected': {
                color: tokens?.accent?.primary,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: tokens?.accent?.primary,
              },
            }}
          >
            <Tab icon={<DeliveryIcon />} iconPosition="start" label="Entregas" />
            <Tab icon={<CostIcon />} iconPosition="start" label="Custos" />
            <Tab icon={<FleetIcon />} iconPosition="start" label="Frota" />
            <Tab icon={<DriverIcon />} iconPosition="start" label="Motoristas" />
          </Tabs>
        </Box>

        {/* Tab: Entregas */}
        {activeTab === 0 && (
          <Grid container spacing={2}>
            {/* Status de Entregas */}
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2.5,
                  backgroundColor: tokens?.surface?.raised,
                  borderRadius: tokens?.radius?.md,
                  border: `1px solid ${tokens?.border?.subtle}`,
                  height: '100%',
                }}
              >
                <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, mb: 2 }}>
                  Status das Entregas
                </Typography>
                <HorizontalBarChart
                  data={[
                    { name: 'Concluidas', value: kpis.deliveries.completed, color: tokens?.semantic?.success },
                    { name: 'Pendentes', value: kpis.deliveries.pending, color: tokens?.semantic?.warning },
                    { name: 'Falhadas', value: kpis.deliveries.failed, color: tokens?.semantic?.error },
                    { name: 'Puladas', value: kpis.deliveries.skipped, color: tokens?.text?.tertiary },
                  ]}
                  tokens={tokens}
                />
              </Paper>
            </Grid>

            {/* Pontualidade */}
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2.5,
                  backgroundColor: tokens?.surface?.raised,
                  borderRadius: tokens?.radius?.md,
                  border: `1px solid ${tokens?.border?.subtle}`,
                  height: '100%',
                }}
              >
                <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, mb: 2 }}>
                  Pontualidade
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ color: tokens?.semantic?.success, fontSize: '56px', fontWeight: 700, lineHeight: 1 }}>
                      {kpis.deliveries.onTimeRate}%
                    </Typography>
                    <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px', mt: 0.5 }}>
                      No Prazo
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    <Typography sx={{ color: tokens?.text?.secondary, fontSize: '12px', mb: 1 }}>
                      {kpis.deliveries.onTimeDeliveries} entregas no prazo
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={kpis.deliveries.onTimeRate}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: tokens?.surface?.elevated,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: tokens?.semantic?.success,
                          borderRadius: 5,
                        },
                      }}
                    />
                    <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '11px', mt: 1, textAlign: 'center' }}>
                      Meta: 95%{kpis.deliveries.onTimeRate >= 95 ? ' - Meta atingida!' : ''}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Metricas de Entregas */}
            <Grid item xs={12} md={4}>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <KpiCard
                    icon={<RouteIcon />}
                    label="Entregas por Rota"
                    value={kpis.deliveries.averagePerPlan}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <KpiCard
                    icon={<FailedIcon />}
                    label="Taxa de Falha"
                    value={kpis.deliveries.failureRate}
                    suffix="%"
                    size="small"
                    trend={{ changePercent: -0.8, direction: 'down' }}
                    isGoodWhenUp={false}
                  />
                </Grid>
                <Grid item xs={6}>
                  <KpiCard
                    icon={<EfficiencyIcon />}
                    label="Distancia/Entrega"
                    value={kpis.efficiency.averageDistancePerDelivery}
                    suffix="km"
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <KpiCard
                    icon={<PendingIcon />}
                    label="Tempo/Entrega"
                    value={kpis.efficiency.averageDurationPerDelivery}
                    suffix="min"
                    size="small"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Tab: Custos */}
        {activeTab === 1 && (
          <Grid container spacing={3}>
            {/* Breakdown de Custos */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: tokens?.surface?.raised,
                  borderRadius: tokens?.radius?.md,
                  border: `1px solid ${tokens?.border?.subtle}`,
                }}
              >
                <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, mb: 2 }}>
                  Composicao de Custos
                </Typography>
                <PieChart data={kpis.costs.costBreakdown} size={140} tokens={tokens} />
              </Paper>
            </Grid>

            {/* Detalhamento */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: tokens?.surface?.raised,
                  borderRadius: tokens?.radius?.md,
                  border: `1px solid ${tokens?.border?.subtle}`,
                }}
              >
                <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, mb: 2 }}>
                  Detalhamento
                </Typography>
                <HorizontalBarChart
                  data={kpis.costs.costBreakdown.map((c) => ({
                    ...c,
                    formattedValue: formatCurrency(c.value),
                  }))}
                  tokens={tokens}
                />
              </Paper>
            </Grid>

            {/* Metricas de Custo */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <KpiCard
                    icon={<CostIcon />}
                    label="Custo por Km"
                    value={formatCurrency(kpis.costs.costPerKm)}
                    size="small"
                    trend={{ changePercent: -2.1, direction: 'down' }}
                    isGoodWhenUp={false}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <KpiCard
                    icon={<DeliveryIcon />}
                    label="Custo por Entrega"
                    value={formatCurrency(kpis.costs.costPerDelivery)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <KpiCard
                    icon={<RouteIcon />}
                    label="Custo Medio/Plano"
                    value={formatCurrency(kpis.costs.averageCostPerPlan)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <KpiCard
                    icon={<TrendingDownIcon />}
                    label="Economia Otimizacao"
                    value={kpis.efficiency.optimizationSavings}
                    suffix="km"
                    description={`${kpis.efficiency.optimizationSavingsPercent}% economia`}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Tab: Frota */}
        {activeTab === 2 && (
          <Grid container spacing={3}>
            {/* Utilizacao */}
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: tokens?.surface?.raised,
                  borderRadius: tokens?.radius?.md,
                  border: `1px solid ${tokens?.border?.subtle}`,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, mb: 2 }}>
                  Utilizacao da Frota
                </Typography>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress
                    variant="determinate"
                    value={kpis.fleet.fleetUtilization}
                    size={120}
                    thickness={8}
                    sx={{
                      color: tokens?.accent?.primary,
                      '& .MuiCircularProgress-circle': {
                        strokeLinecap: 'round',
                      },
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography sx={{ color: tokens?.text?.primary, fontSize: '24px', fontWeight: 700 }}>
                      {kpis.fleet.fleetUtilization}%
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px', mt: 2 }}>
                  {kpis.fleet.vehiclesUsed} de {kpis.fleet.totalVehicles} veiculos ativos
                </Typography>
              </Paper>
            </Grid>

            {/* Top Veiculos */}
            <Grid item xs={12} md={8}>
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: tokens?.surface?.raised,
                  borderRadius: tokens?.radius?.md,
                  border: `1px solid ${tokens?.border?.subtle}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TrophyIcon sx={{ color: tokens?.semantic?.warning }} />
                  <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600 }}>
                    Top Veiculos por Entregas
                  </Typography>
                </Box>
                <RankingList
                  items={kpis.fleet.topVehicles.map((v) => ({
                    id: v.id,
                    name: v.name,
                    value: v.deliveries,
                  }))}
                  valueLabel="entregas"
                  tokens={tokens}
                />
              </Paper>
            </Grid>

            {/* Metricas de Frota */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <KpiCard
                    icon={<FleetIcon />}
                    label="Veiculos Totais"
                    value={kpis.fleet.totalVehicles}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <KpiCard
                    icon={<FleetIcon />}
                    label="Veiculos Ativos"
                    value={kpis.fleet.vehiclesUsed}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <KpiCard
                    icon={<RouteIcon />}
                    label="Viagens/Veiculo"
                    value={kpis.fleet.averageTripsPerVehicle}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <KpiCard
                    icon={<EfficiencyIcon />}
                    label="Distancia Total"
                    value={kpis.efficiency.totalDistance.toLocaleString()}
                    suffix="km"
                    size="small"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Tab: Motoristas */}
        {activeTab === 3 && (
          <Grid container spacing={3}>
            {/* Estatisticas Gerais */}
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: tokens?.surface?.raised,
                  borderRadius: tokens?.radius?.md,
                  border: `1px solid ${tokens?.border?.subtle}`,
                }}
              >
                <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, mb: 2 }}>
                  Estatisticas Gerais
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '11px', mb: 0.5 }}>
                      MOTORISTAS ATIVOS
                    </Typography>
                    <Typography sx={{ color: tokens?.text?.primary, fontSize: '32px', fontWeight: 700 }}>
                      {kpis.drivers.totalDrivers}
                    </Typography>
                  </Box>
                  <Divider sx={{ borderColor: tokens?.border?.subtle }} />
                  <Box>
                    <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '11px', mb: 0.5 }}>
                      EFICIENCIA MEDIA
                    </Typography>
                    <Typography sx={{ color: tokens?.accent?.primary, fontSize: '24px', fontWeight: 600 }}>
                      {kpis.drivers.averageEfficiency} <span style={{ fontSize: '14px' }}>entregas/hora</span>
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '11px', mb: 0.5 }}>
                      TAXA DE CONCLUSAO MEDIA
                    </Typography>
                    <Typography sx={{ color: tokens?.semantic?.success, fontSize: '24px', fontWeight: 600 }}>
                      {kpis.drivers.averageCompletionRate}%
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Top Motoristas */}
            <Grid item xs={12} md={8}>
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: tokens?.surface?.raised,
                  borderRadius: tokens?.radius?.md,
                  border: `1px solid ${tokens?.border?.subtle}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TrophyIcon sx={{ color: tokens?.semantic?.warning }} />
                  <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600 }}>
                    Top Motoristas por Eficiencia
                  </Typography>
                </Box>
                <RankingList
                  items={kpis.drivers.topDrivers.map((d) => ({
                    id: d.id,
                    name: d.name,
                    value: d.efficiency,
                    formattedValue: `${d.efficiency} ent/h (${d.completionRate}%)`,
                  }))}
                  valueLabel=""
                  tokens={tokens}
                />
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Footer */}
        <Box
          sx={{
            mt: 4,
            pt: 2,
            borderTop: `1px solid ${tokens?.border?.subtle}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
            Dados atualizados em: {new Date(kpis.meta.calculatedAt).toLocaleString('pt-BR')}
          </Typography>
          <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
            {kpis.meta.plansCount} planos analisados no periodo
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default RoutingDashboardPage;
